/**
 * Mapový kontroler nad Leafletem:
 *  - dlaždice (světlé/tmavé, s atribucí),
 *  - tahatelné markery A/B,
 *  - vrstvy: trasy (barevné segmenty), zastávky MHD, zóny výhodnosti,
 *  - legenda a přepínač vrstev,
 *  - režim „klikni pro výběr bodu".
 */
import L from 'leaflet';
import { boundsAround } from '../core/geo';
import { cellColor } from '../core/zones';
import type {
  Itinerary,
  LatLng,
  ModeProfile,
  NamedPoint,
  TransitNetwork,
  ZoneGrid,
} from '../core/types';
import { el, clear } from './dom';

export type PickTarget = 'origin' | 'destination' | 'stop' | null;

export interface MapCallbacks {
  onPick(target: Exclude<PickTarget, null>, p: LatLng): void;
  onMarkerDragged(which: 'origin' | 'destination', p: LatLng): void;
  onLayersChanged(): void;
}

export interface LayerVisibility {
  routes: boolean;
  stops: boolean;
  zones: boolean;
}

const TILE_LIGHT = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
const TILE_DARK = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
const TILE_ATTR =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>' +
  ' · trasy <a href="https://project-osrm.org/">OSRM</a> · hledání <a href="https://nominatim.org/">Nominatim</a>';

export class MapView {
  readonly map: L.Map;
  private tiles: L.TileLayer;
  private markerA: L.Marker | null = null;
  private markerB: L.Marker | null = null;
  private routesLayer = L.layerGroup();
  private stopsLayer = L.layerGroup();
  private zoneOverlay: L.ImageOverlay | null = null;
  private pickTarget: PickTarget = null;
  private hintEl: HTMLElement | null = null;
  private legendBox: HTMLElement;
  private layersBox: HTMLElement;
  private cb: MapCallbacks;
  visibility: LayerVisibility = { routes: true, stops: true, zones: true };
  private tileErrors = 0;
  private tileNotice: HTMLElement | null = null;

  constructor(container: HTMLElement, wrap: HTMLElement, theme: 'light' | 'dark', cb: MapCallbacks) {
    this.cb = cb;
    this.map = L.map(container, {
      center: [50.0875, 14.4213], // Praha jako neutrální start
      zoom: 12,
      zoomControl: true,
      attributionControl: true,
    });
    this.tiles = L.tileLayer(theme === 'dark' ? TILE_DARK : TILE_LIGHT, {
      attribution: TILE_ATTR,
      maxZoom: 19,
      subdomains: 'abcd',
    }).addTo(this.map);
    this.tiles.on('tileerror', () => this.onTileError(wrap));
    this.routesLayer.addTo(this.map);
    this.stopsLayer.addTo(this.map);

    this.map.on('click', (e: L.LeafletMouseEvent) => {
      if (!this.pickTarget) return;
      const t = this.pickTarget;
      this.setPickTarget(null, wrap);
      cb.onPick(t, { lat: e.latlng.lat, lng: e.latlng.lng });
    });

    // Zastávky se kreslí jen ve výřezu — po posunu/zoomu je obnovíme.
    this.map.on('moveend zoomend', () => this.refreshStopsViewport());

    // Vlastní ovládací prvky (legenda + vrstvy) jako Leaflet controls.
    const LayersControl = L.Control.extend({
      onAdd: () => {
        this.layersBox.addEventListener('dblclick', (e) => e.stopPropagation());
        L.DomEvent.disableClickPropagation(this.layersBox);
        L.DomEvent.disableScrollPropagation(this.layersBox);
        return this.layersBox;
      },
    });
    const LegendControl = L.Control.extend({
      onAdd: () => {
        L.DomEvent.disableClickPropagation(this.legendBox);
        L.DomEvent.disableScrollPropagation(this.legendBox);
        return this.legendBox;
      },
    });
    this.layersBox = el('div', { className: 'map-control', role: 'group', 'aria-label': 'Mapové vrstvy' });
    this.legendBox = el('div', { className: 'map-control', 'aria-label': 'Legenda' });
    new LayersControl({ position: 'topright' }).addTo(this.map);
    new LegendControl({ position: 'bottomright' }).addTo(this.map);
    this.renderLayersControl();
    this.legendBox.style.display = 'none';
  }

  private onTileError(wrap: HTMLElement): void {
    this.tileErrors += 1;
    if (this.tileErrors === 8 && !this.tileNotice) {
      this.tileNotice = el(
        'div',
        { className: 'map-hint', role: 'status' },
        'Mapové podklady se nedaří načíst (offline?). Plánování dál funguje — trasy uvidíte na šedém pozadí.',
      );
      wrap.appendChild(this.tileNotice);
      window.setTimeout(() => {
        this.tileNotice?.remove();
        this.tileNotice = null;
      }, 9000);
    }
  }

  setTheme(theme: 'light' | 'dark'): void {
    this.tiles.setUrl(theme === 'dark' ? TILE_DARK : TILE_LIGHT);
  }

  /* ---------------- výběr bodu kliknutím ---------------- */

  setPickTarget(target: PickTarget, wrap: HTMLElement): void {
    this.pickTarget = target;
    this.hintEl?.remove();
    this.hintEl = null;
    const mapEl = this.map.getContainer();
    mapEl.style.cursor = target ? 'crosshair' : '';
    if (target) {
      const label =
        target === 'origin'
          ? 'Klikněte do mapy pro umístění bodu A (Esc zruší)'
          : target === 'destination'
            ? 'Klikněte do mapy pro umístění bodu B (Esc zruší)'
            : 'Klikněte do mapy pro přidání zastávky (Esc zruší)';
      this.hintEl = el('div', { className: 'map-hint', role: 'status' }, label);
      wrap.appendChild(this.hintEl);
      const onEsc = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') {
          this.setPickTarget(null, wrap);
          document.removeEventListener('keydown', onEsc);
        }
      };
      document.addEventListener('keydown', onEsc);
    }
  }

  get picking(): PickTarget {
    return this.pickTarget;
  }

  /* ---------------- markery A/B ---------------- */

  setPoints(a: NamedPoint | null, b: NamedPoint | null): void {
    this.markerA = this.syncMarker(this.markerA, a, 'a');
    this.markerB = this.syncMarker(this.markerB, b, 'b');
  }

  private syncMarker(marker: L.Marker | null, p: NamedPoint | null, which: 'a' | 'b'): L.Marker | null {
    if (!p) {
      marker?.remove();
      return null;
    }
    if (!marker) {
      const icon = L.divIcon({
        className: '',
        html: `<div class="marker-pin marker-pin--${which}"><span>${which.toUpperCase()}</span></div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 28],
      });
      marker = L.marker([p.lat, p.lng], {
        icon,
        draggable: true,
        keyboard: true,
        title: which === 'a' ? 'Bod A (přetažením upravíte)' : 'Bod B (přetažením upravíte)',
      }).addTo(this.map);
      marker.on('dragend', () => {
        const ll = marker!.getLatLng();
        this.cb.onMarkerDragged(which === 'a' ? 'origin' : 'destination', {
          lat: ll.lat,
          lng: ll.lng,
        });
      });
    } else {
      marker.setLatLng([p.lat, p.lng]);
    }
    marker.bindTooltip(p.name || (which === 'a' ? 'Start' : 'Cíl'), { direction: 'top', offset: [0, -26] });
    return marker;
  }

  fitToPoints(a: NamedPoint | null, b: NamedPoint | null): void {
    const pts = [a, b].filter((p): p is NamedPoint => !!p);
    if (pts.length === 0) return;
    if (pts.length === 1) {
      this.map.setView([pts[0].lat, pts[0].lng], Math.max(this.map.getZoom(), 13));
      return;
    }
    const bb = boundsAround(pts, 0.6);
    this.map.fitBounds(
      [
        [bb.south, bb.west],
        [bb.north, bb.east],
      ],
      { padding: [40, 40] },
    );
  }

  /* ---------------- trasy ---------------- */

  renderRoutes(itineraries: Itinerary[], selectedId: string | null, solo: boolean): void {
    this.routesLayer.clearLayers();
    if (!this.visibility.routes) return;
    const list = solo && selectedId ? itineraries.filter((i) => i.id === selectedId) : itineraries;
    // Nevybrané vykreslíme první (pod vybranou).
    const ordered = [...list].sort((x, y) => Number(x.id === selectedId) - Number(y.id === selectedId));
    for (const it of ordered) {
      const isSel = it.id === selectedId;
      for (const seg of it.segments) {
        if (seg.geometry.length < 2) continue;
        const latlngs = seg.geometry.map((p) => [p.lat, p.lng] as [number, number]);
        // Podklad pro čitelnost vybrané trasy.
        if (isSel) {
          L.polyline(latlngs, {
            color: '#ffffff',
            weight: 9,
            opacity: 0.85,
            interactive: false,
          }).addTo(this.routesLayer);
        }
        const line = L.polyline(latlngs, {
          color: seg.color,
          weight: isSel ? 6 : 3.5,
          opacity: isSel ? 1 : selectedId ? 0.35 : 0.8,
          dashArray: seg.realGeometry ? undefined : '7 7',
          lineCap: 'round',
        }).addTo(this.routesLayer);
        line.bindTooltip(
          `${it.name} — ${seg.label}${seg.realGeometry ? '' : ' (odhad geometrie)'}`,
          { sticky: true },
        );
      }
      // Body čekání/přestupů.
      for (const seg of it.segments) {
        if ((seg.kind === 'wait' || seg.kind === 'transit') && isSel) {
          const p = seg.kind === 'wait' ? seg.from : seg.from;
          L.circleMarker([p.lat, p.lng], {
            radius: 5,
            color: seg.kind === 'wait' ? '#b3700a' : seg.color,
            fillColor: '#fff',
            fillOpacity: 1,
            weight: 3,
          })
            .bindTooltip(seg.kind === 'wait' ? `Čekání: ${seg.label}` : `Nástup: ${seg.label}`)
            .addTo(this.routesLayer);
        }
      }
    }
  }

  /* ---------------- zastávky ---------------- */

  /**
   * Zastávky kreslíme jako circleMarkery na sdíleném canvas rendereru
   * a JEN ty ve viditelném výřezu (+ rezerva), s limitem počtu.
   * U reálných sítí (PID: tisíce zastávek) by DOM markery mapu zadusily.
   */
  private lastStopsNet: TransitNetwork | null = null;
  private lastPtEnabled = false;
  private stopsRenderer = L.canvas({ padding: 0.3 });

  renderStops(net: TransitNetwork, ptEnabled: boolean): void {
    this.lastStopsNet = net;
    this.lastPtEnabled = ptEnabled;
    this.stopsLayer.clearLayers();
    if (!this.visibility.stops || !ptEnabled) return;
    // Na velkém oddálení by byly zastávky jen šum.
    if (net.stops.length > 200 && this.map.getZoom() < 12) return;
    const bounds = this.map.getBounds().pad(0.2);
    const MAX_STOPS = 600;
    let shown = 0;
    for (const s of net.stops) {
      if (!bounds.contains([s.lat, s.lng])) continue;
      if (++shown > MAX_STOPS) break;
      L.circleMarker([s.lat, s.lng], {
        renderer: this.stopsRenderer,
        radius: 3.5,
        color: '#ffffff',
        weight: 1.5,
        fillColor: '#d6293a',
        fillOpacity: 0.9,
      })
        .bindTooltip(`${s.name}${net.source === 'demo' ? ' (demo)' : ''}`)
        .addTo(this.stopsLayer);
    }
  }

  /** Po posunu/zoomu se zastávky ve výřezu překreslí (viz konstruktor). */
  private refreshStopsViewport(): void {
    if (this.lastStopsNet) this.renderStops(this.lastStopsNet, this.lastPtEnabled);
  }

  /* ---------------- zóny ---------------- */

  renderZones(grid: ZoneGrid | null, modes: ModeProfile[], opacity = 0.62): void {
    this.zoneOverlay?.remove();
    this.zoneOverlay = null;
    if (!grid || !this.visibility.zones) {
      this.updateLegend(grid, modes, []);
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = grid.cols;
    canvas.height = grid.rows;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = ctx.createImageData(grid.cols, grid.rows);
    for (let r = 0; r < grid.rows; r++) {
      for (let c = 0; c < grid.cols; c++) {
        const cell = grid.cells[r * grid.cols + c];
        const rgb = cellColor(grid, cell.value, cell.bestModeId, modes);
        // Canvas y roste dolů; řádek 0 mřížky je jih → zrcadlíme.
        const y = grid.rows - 1 - r;
        const idx = (y * grid.cols + c) * 4;
        if (rgb) {
          img.data[idx] = rgb[0];
          img.data[idx + 1] = rgb[1];
          img.data[idx + 2] = rgb[2];
          // Plynulé vyblednutí u limitů vzdálenosti (žádná tvrdá hrana).
          img.data[idx + 3] = Math.round(165 * (cell.alpha ?? 1));
        } else {
          img.data[idx + 3] = 0;
        }
      }
    }
    ctx.putImageData(img, 0, 0);
    this.zoneOverlay = L.imageOverlay(canvas.toDataURL(), [
      [grid.bounds.south, grid.bounds.west],
      [grid.bounds.north, grid.bounds.east],
    ], { opacity, interactive: false, className: 'zone-overlay' });
    this.zoneOverlay.addTo(this.map);
    this.zoneOverlay.bringToBack();
  }

  /* ---------------- legenda + vrstvy ---------------- */

  updateLegend(grid: ZoneGrid | null, modes: ModeProfile[], usedSegments: Array<{ color: string; label: string; dashed: boolean }>): void {
    clear(this.legendBox);
    const parts: HTMLElement[] = [];
    if (usedSegments.length > 0 && this.visibility.routes) {
      parts.push(el('div', { className: 'map-control__title' }, 'Trasy'));
      const seen = new Set<string>();
      for (const s of usedSegments) {
        const key = `${s.color}|${s.label}`;
        if (seen.has(key)) continue;
        seen.add(key);
        parts.push(
          el(
            'div',
            { className: 'legend-item' },
            el('span', {
              className: 'legend-line',
              style: `background:${s.dashed ? `repeating-linear-gradient(90deg, ${s.color} 0 5px, transparent 5px 9px)` : s.color}`,
            }),
            el('span', {}, s.label),
          ),
        );
      }
      parts.push(
        el('div', { className: 'muted', style: 'margin-top:4px' }, 'Čárkovaně = odhadovaná geometrie'),
      );
    }
    if (grid && this.visibility.zones) {
      parts.push(el('div', { className: 'map-control__title', style: usedSegments.length ? 'margin-top:10px' : '' }, `Zóny: odkud se vyplatí co (${zoneTitle(grid.metric)})`));
      for (const item of grid.legend) {
        parts.push(
          el(
            'div',
            { className: 'legend-item' },
            el('span', { className: 'legend-swatch', style: `background:${item.color}` }),
            el('span', {}, item.label),
          ),
        );
      }
      parts.push(
        el(
          'div',
          { className: 'muted', style: 'margin-top:4px' },
          'Odhadový model · cesta do bodu B · bledé = na hraně limitu, nebo velmi dlouhý čas',
        ),
      );
    }
    if (parts.length === 0) {
      this.legendBox.style.display = 'none';
      return;
    }
    this.legendBox.style.display = '';
    this.legendBox.append(...parts);
  }

  renderLayersControl(): void {
    clear(this.layersBox);
    const mk = (key: keyof LayerVisibility, label: string): HTMLElement =>
      el(
        'label',
        { className: 'row switch' },
        el('input', {
          type: 'checkbox',
          checked: this.visibility[key],
          onchange: (e: Event) => {
            this.visibility[key] = (e.target as HTMLInputElement).checked;
            this.cb.onLayersChanged();
          },
        }),
        el('span', {}, label),
      );
    this.layersBox.append(
      el('div', { className: 'map-control__title' }, 'Vrstvy'),
      mk('routes', 'Trasy'),
      mk('stops', 'Zastávky MHD'),
      mk('zones', 'Zóny výhodnosti'),
    );
  }

  invalidate(): void {
    this.map.invalidateSize();
  }
}

function zoneTitle(metric: string): string {
  const titles: Record<string, string> = {
    bestMode: 'nejlepší režim',
    weighted: 'vážená kombinace',
    time: 'čas',
    wait: 'čekání',
    cost: 'cena',
    emissions: 'emise',
    effort: 'fyzická náročnost',
    comfort: 'komfort',
    transfers: 'přestupy',
    uncertainty: 'nejistota',
  };
  return titles[metric] ?? metric;
}
